import {AlertSubscription, ViewSource} from '../../generated/graphql';
import {Views2ViewQuery} from '../../generated/graphql';
import * as CustomRunColorsTypes from './customRunColors/types';
import * as DiscussionCommentTypes from './discussionComment/types';
import * as DiscussionThreadTypes from './discussionThread/types';
import * as FilterTypes from './filter/types';
import * as GroupPageTypes from './groupPage/types';
import * as GroupSelectionsTypes from './groupSelections/types';
import * as InteractStateTypes from './interactState/types';
import * as MarkdownBlockTypes from './markdownBlock/types';
import * as PanelNormTypes from './panel/types';
import * as PanelBankConfigTypes from './panelBankConfig/types';
import * as PanelBankSectionConfigTypes from './panelBankSectionConfig/types';
import * as PanelsTypes from './panels/types';
import * as PanelSettingsTypes from './panelSettings/types';
import * as ProjectPageTypes from './projectPage/types';
import * as ReportTypes from './report/types';
import * as ReportDraftTypes from './reportDraft/types';
import * as RunPageTypes from './runPage/types';
import * as RunSetTypes from './runSet/types';
import * as SectionTypes from './section/types';
import * as SortTypes from './sort/types';
import * as SweepPageTypes from './sweepPage/types';
import * as TempSelectionsTypes from './tempSelections/types';

export interface LoadMetadataListParams {
  entityName: string;
  projectName: string;
  viewType: string;
  viewName?: string;
  userName?: string;
}

export type AccessToken = NonNullable<
  NonNullable<Views2ViewQuery['view']>['accessTokens']
>[number];

export interface View {
  cid: string; // client id
  id?: string; // server id
  type: ViewType;
  name: string;
  displayName: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
  updatedBy?: {
    id: string;
    username: string;
    name: string;
  };
  user: {
    id: string;
    username: string;
    name: string;
    photoUrl?: string;
    admin: boolean;
  };
  entityName: string;
  project?: {
    id?: string;
    name: string;
    entityName: string;
    readOnly: boolean;
  };
  spec?: string;
  previewUrl?: string;
  coverUrl?: string;
  starCount?: number;
  starred?: boolean;
  parentId?: string;
  locked: boolean;
  viewCount?: number;
  alertSubscription?: AlertSubscription;
  accessTokens?: AccessToken[];
}

export interface SaveableView {
  id?: string;
  type?: ViewType;
  name?: string;
  displayName?: string;
  project?: {
    name?: string;
    entityName: string;
  };
  description?: string;
  spec?: any;
  previewUrl?: string;
  coverUrl?: string;
  parentId?: string;
  locked?: boolean;
  createdUsing?: ViewSource;
}

export type LoadableView = Omit<View, 'spec'> & {
  loading: boolean;
  autoSave: boolean;
  saving: boolean;
  modified: boolean;
  starLoading: boolean;
  partRef?: ViewPartRefs; // pointer to normalized version
  panelCommentsEnabled: boolean;
};

export type ObjSchema =
  | ProjectPageTypes.ProjectPageObjSchema
  | ReportTypes.ReportObjSchema
  | ReportDraftTypes.ReportDraftObjSchema
  | RunPageTypes.RunPageObjSchema
  | RunSetTypes.RunSetObjSchema
  | SectionTypes.SectionObjSchema
  | MarkdownBlockTypes.MarkdownBlockObjSchema
  | SortTypes.SortObjSchema
  | FilterTypes.FilterObjSchema
  | GroupSelectionsTypes.GroupSelectionsObjSchema
  | GroupPageTypes.GroupPageObjSchema
  | SweepPageTypes.SweepPageObjSchema
  | PanelsTypes.PanelsObjSchema
  | PanelBankConfigTypes.PanelBankConfigObjSchema
  | PanelBankSectionConfigTypes.PanelBankSectionConfigObjSchema
  | PanelNormTypes.PanelObjSchema
  | PanelSettingsTypes.PanelSettingsObjSchema
  | CustomRunColorsTypes.CustomRunColorsObjSchema
  | TempSelectionsTypes.TempSelectionObjSchema
  | InteractStateTypes.InteractStateObjSchema
  | DiscussionThreadTypes.DiscussionThreadObjSchema
  | DiscussionCommentTypes.DiscussionCommentObjSchema;

export type ObjType = ObjSchema['type'];

// A subset of objs are views, these are the objs that can be
// saved and loaded for the server.
// You'll get type-errors elsewhere if this is not a subset of ObjType
export const VIEW_TYPES = [
  'project-view',
  'group-view',
  'sweep-view',
  'run-view',
  'runs',
  'runs/draft',
] as const;
export type ViewType = typeof VIEW_TYPES[number];

type ViewObjSchemaHelper<S extends ObjSchema> = S extends {type: ViewType}
  ? S
  : never;
export type ViewObjSchema = ViewObjSchemaHelper<ObjSchema>;

export interface ViewRefFromObjSchema<O extends ViewObjSchema> {
  type: O['type'];
  id: string;
}

export type ViewRef = ViewRefFromObjSchema<ViewObjSchema>;

export interface PartRefFields {
  viewID: string;
  id: string;
}

export type PartRefFromObjSchema<O extends ObjSchema> = Pick<O, 'type'> &
  PartRefFields;

export type AllPartRefHelper<P> = P extends ObjSchema
  ? {
      type: P['type'];
    } & PartRefFields
  : never;
export type AllPartRefs = AllPartRefHelper<ObjSchema>;
export type ViewPartRefs = AllPartRefHelper<ViewObjSchema>;

export type AllWholeWithTypesHelper<T> = T extends ObjType
  ? {
      type: T;
      whole: WholeFromType<T>;
    }
  : never;
export type AllWholeWithTypes = AllWholeWithTypesHelper<ObjType>;

export type PartRefFromType<S extends ObjType> = S extends ObjType
  ? {type: S} & PartRefFields
  : never;

type ObjSchemaFromTypeHelper<
  T extends ObjType,
  S extends ObjSchema
> = S extends {type: T} ? S : never;

export type ObjSchemaFromType<T extends ObjType> = ObjSchemaFromTypeHelper<
  T,
  ObjSchema
>;

export type PartFromType<T extends ObjType> = ObjSchemaFromType<T>['part'];
export type WholeFromType<T extends ObjType> = ObjSchemaFromType<T>['whole'];
export type WholeFromTypeWithRef<T extends ObjType> = WholeFromType<T> & {
  ref: PartRefFromType<T>;
};
